import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { validateUploadLink, submitBatch } from '../../utils/publicUploadApi';

const ICON_UPLOAD = '↑';
const ICON_CHECK = '✓';

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── Shell ── */
function PortalShell({ providerName, expiresAt, children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', fontFamily: 'Inter, sans-serif' }}>
      {/* Top bar */}
      <Box sx={{
        height: 56, borderBottom: '0.4px solid #000',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 4,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 2, bgcolor: '#000',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 900, fontSize: 13,
          }}>M</Box>
          <Typography fontWeight={700} fontSize={15}>Meduri</Typography>
        </Box>
        <Typography fontSize={12} color="text.secondary">
          Secure link{expiresAt ? ` · expires ${formatDate(expiresAt)}` : ''}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', px: 4, py: 4 }}>
        {/* Provider strip */}
        {providerName && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 3 }}>
            <Box>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                Uploading on behalf of
              </Typography>
              <Typography fontWeight={600} fontSize={16}>{providerName}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              HIPAA-compliant · TLS 1.3 · Private storage
            </Typography>
          </Box>
        )}

        <Typography variant="h4" fontWeight={700} lineHeight={1} mb={1.25}>
          Secure Bulk Records Upload
        </Typography>
        <Typography fontSize={14} color="text.secondary" mb={3.5} maxWidth={560} lineHeight={1.6}>
          Drop in any patient records, EOBs, denial letters, or supporting documents. Our team
          will sort, organize, and route each file. You don't need to label anything.
        </Typography>

        {children}

        {/* Footer */}
        <Box sx={{
          mt: 4, pt: 2, borderTop: '0.4px solid rgba(0,0,0,0.15)',
          display: 'flex', justifyContent: 'space-between', fontSize: 12,
        }}>
          <Typography variant="caption" color="text.secondary">
            Files are scanned for malware before processing.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Need help?{' '}
            <Box component="a" href="mailto:support@meduri.health" sx={{ color: '#000' }}>
              support@meduri.health
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/* ── File row ── */
function FileRow({ file, onRemove }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 1.75, py: 1.25,
      borderTop: '1px solid rgba(0,0,0,0.06)',
      '&:first-of-type': { borderTop: 'none' },
      bgcolor: 'inherit',
    }}>
      <Typography fontSize={13} sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {formatBytes(file.size)}
      </Typography>
      <Box
        component="button"
        onClick={() => onRemove(file)}
        sx={{
          border: 'none', background: 'none', cursor: 'pointer',
          color: '#94a3b8', fontSize: 16, lineHeight: 1, p: 0, flexShrink: 0,
          '&:hover': { color: '#000' },
        }}
      >×</Box>
    </Box>
  );
}

/* ── Receipt row ── */
function ReceiptRow({ label, value }) {
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      px: 2, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 14,
    }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

/* ── Main page ── */
export default function PublicUploadPage() {
  const { token } = useParams();

  const [linkMeta, setLinkMeta] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [files, setFiles] = useState([]);
  const [uploaderName, setUploaderName] = useState('');
  const [uploaderEmail, setUploaderEmail] = useState('');
  const [note, setNote] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    validateUploadLink(token)
      .then(res => setLinkMeta(res.data))
      .catch(err => {
        if (err.response?.status === 410) {
          setLinkError('This upload link has expired or been deactivated.');
        } else if (err.response?.status === 404) {
          setLinkError('This upload link is invalid.');
        } else {
          setLinkError('Unable to load this upload link. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const onDrop = useCallback((accepted) => {
    const maxMb = (linkMeta?.max_file_size_mb || 25) * 1024 * 1024;
    const valid = accepted.filter(f => f.size <= maxMb);
    const over = accepted.filter(f => f.size > maxMb);
    if (over.length) {
      setSubmitError(`${over.length} file(s) exceed the ${linkMeta?.max_file_size_mb || 25} MB limit and were skipped.`);
    }
    setFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      const fresh = valid.filter(f => !names.has(f.name));
      return [...prev, ...fresh].slice(0, linkMeta?.max_files || 200);
    });
  }, [linkMeta]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const removeFile = (target) => setFiles(prev => prev.filter(f => f !== target));
  const resetForNextBatch = () => {
    setReceipt(null);
    setFiles([]);
    setUploaderName('');
    setUploaderEmail('');
    setNote('');
    setProgress(0);
    setSubmitting(false);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (files.length === 0 || submitting) return;
    if (linkMeta?.require_uploader_identity && (!uploaderName.trim() || !uploaderEmail.trim())) {
      setSubmitError('Your name and email are required for this upload link.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setProgress(0);

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    if (uploaderName.trim()) formData.append('uploader_name', uploaderName.trim());
    if (uploaderEmail.trim()) formData.append('uploader_email', uploaderEmail.trim());
    if (note.trim()) formData.append('note', note.trim());

    try {
      const res = await submitBatch(token, formData, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      setReceipt(res.data);
      setSubmitting(false);
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setSubmitting(false);
    }
  };

  /* ── Loading / error states ── */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (linkError) {
    return (
      <PortalShell>
        <Alert severity="error" sx={{ mt: 4 }}>{linkError}</Alert>
      </PortalShell>
    );
  }

  /* ── Confirmation ── */
  if (receipt) {
    return (
      <PortalShell providerName={receipt.provider_name} expiresAt={linkMeta?.expires_at}>
        <Box sx={{ border: '1px solid #000', borderRadius: 1, p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 3 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '50%', bgcolor: '#E6F4EC',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#1F8A4C', fontSize: 22,
            }}>{ICON_CHECK}</Box>
            <Box>
              <Typography fontWeight={700} fontSize={24} lineHeight={1}>Batch received</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.75}>
                Our team will begin sorting within 1 business day.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ border: '0.4px solid #000' }}>
            <ReceiptRow label="Batch reference" value={
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {receipt.batch_ref}
              </Box>
            } />
            <ReceiptRow label="Files received" value={`${receipt.file_count} files · ${formatBytes(receipt.total_size_bytes)}`} />
            <ReceiptRow label="Provider" value={receipt.provider_name} />
            {receipt.uploader_name && (
              <ReceiptRow label="Submitted by" value={
                receipt.uploader_email
                  ? `${receipt.uploader_name} · ${receipt.uploader_email}`
                  : receipt.uploader_name
              } />
            )}
            <ReceiptRow label="Submitted at" value={new Date(receipt.submitted_at).toLocaleString()} />
          </Box>

          {receipt.uploader_email && (
            <Typography variant="body2" color="text.secondary" mt={2.5} lineHeight={1.6}>
              We've emailed a copy of this receipt to{' '}
              <Box component="strong" sx={{ color: '#000' }}>{receipt.uploader_email}</Box>.
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 3.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={resetForNextBatch}>
            Upload Another Batch
          </Button>
        </Box>
      </PortalShell>
    );
  }

  /* ── Upload form ── */
  const totalBytes = files.reduce((s, f) => s + f.size, 0);
  const hasRequiredIdentity = !linkMeta?.require_uploader_identity || Boolean(uploaderName.trim() && uploaderEmail.trim());
  const canSubmit = files.length > 0 && hasRequiredIdentity && !submitting;

  return (
    <PortalShell providerName={linkMeta?.provider_name} expiresAt={linkMeta?.expires_at}>
      {submitError && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Dropzone */}
      {files.length === 0 ? (
        <Box
          {...getRootProps()}
          sx={{
            border: '1px dashed #000',
            bgcolor: isDragActive ? '#F0F4FF' : '#F8F9FC',
            borderRadius: 1,
            px: 4, py: 8,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 0.15s',
            mb: 2.5,
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{
            width: 56, height: 56, border: '1px solid #000', borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            mb: 2.5, fontSize: 22,
          }}>{ICON_UPLOAD}</Box>
          <Typography fontWeight={700} fontSize={20} lineHeight={1} mb={1}>
            {isDragActive ? 'Drop files here' : 'Drag files here, or click to browse'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            PDF, JPG, PNG, TIFF, DOCX · up to {linkMeta?.max_files || 200} files · {linkMeta?.max_file_size_mb || 25} MB each
          </Typography>
          <Button variant="outlined">Choose Files</Button>
        </Box>
      ) : (
        /* Compact dropzone once files are added */
        <Box
          {...getRootProps()}
          sx={{
            border: '1px dashed #000', bgcolor: '#F8F9FC', borderRadius: 1,
            px: 2.5, py: 1.75, display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', mb: 2.5, cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography fontSize={14}>{ICON_UPLOAD} Drop more files, or</Typography>
            <Button size="small" variant="outlined" onClick={e => e.stopPropagation()}>Add Files</Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {files.length} file{files.length !== 1 ? 's' : ''} · {formatBytes(totalBytes)}
          </Typography>
        </Box>
      )}

      {/* File list */}
      {files.length > 0 && (
        <Box sx={{ border: '1px solid #d1d5db', borderRadius: 2, maxHeight: 320, overflow: 'auto', mb: 2.5 }}>
          {files.map((f, i) => (
            <FileRow key={`${f.name}-${i}`} file={f} onRemove={removeFile} />
          ))}
        </Box>
      )}

      {/* Upload progress */}
      {submitting && (
        <Box mb={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" fontWeight={700}>Uploading…</Typography>
            <Typography variant="caption" color="text.secondary">{progress}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 999 }} />
        </Box>
      )}

      {/* Optional identity + note fields */}
      {!submitting && (
        <>
          {linkMeta?.require_uploader_identity && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 2.5 }}>
              <TextField
                label="Your name"
                value={uploaderName}
                onChange={e => setUploaderName(e.target.value)}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={uploaderEmail}
                onChange={e => setUploaderEmail(e.target.value)}
                fullWidth
                required
                size="small"
              />
            </Box>
          )}
          <TextField
            label="Batch note (optional)"
            placeholder="e.g. 'all denied claims from April'"
            value={note}
            onChange={e => setNote(e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={{ mb: 3.5 }}
          />
        </>
      )}

      {/* Footer action bar */}
      <Box sx={{
        pt: 2.5, borderTop: '0.4px solid #000',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Typography variant="caption" color="text.secondary">
          {files.length === 0 ? '0 files selected' : `${files.length} file${files.length !== 1 ? 's' : ''} selected`}
        </Typography>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Uploading…' : 'Submit Batch'}
        </Button>
      </Box>
    </PortalShell>
  );
}
